// Shared lifecycle-transition dialog. One primitive consumed by both
// the milestone progress flow (ProgressMilestoneModal wrapper) and the
// deliverable acceptance gate (AcceptanceGateDialog wrapper). Owns the
// modal scrim, header, outcome chip rows, contextual comment field,
// date input with min-guard, soft-warning slot, and Save/Cancel.
//
// The dialog is pure — callers supply the per-call outcome set and
// status context. It does not read or know about Milestone or
// Deliverable shapes; that translation lives in the wrappers.

import { useMemo, useState } from "react";
import Modal from "../../../common/Modal";
import Button from "../../../common/Button";
import Alert from "../../../common/Alert";
import { ApiError } from "../../../../services/api";

export type BadgeVariant =
  | "draft"
  | "active"
  | "outlinedAmber"
  | "outlinedRed"
  | "outlinedRose"
  | "filledRed"
  | "closed";

export interface SoftWarningCtx {
  failedAcceptanceCount?: number;
}

export interface Outcome {
  /** Stable id used as the test selector and as the radio value. */
  id: string;
  /** Visible chip label, e.g. "Mark accepted". */
  label: string;
  /** Pill colour register for the outcome's resulting state. */
  pill: BadgeVariant;
  /** Visible pill text, e.g. "Accepted". */
  pillLabel: string;
  /** Short prose under the label. */
  description: string;
  /** When true, the comment textarea is required to save. */
  requiresComment: boolean;
  /** Adapts the comment field label: "conditions" / "reason" / undefined. */
  commentLabel?: "conditions" | "reason";
  /** Lifecycle target this outcome emits (event type or milestone field). */
  targetState: string;
  /** Non-blocking warning. Returns the message string, or null for none. */
  softWarning?: (ctx: SoftWarningCtx) => string | null;
}

/** Per-element testid overrides. Lets a wrapper (e.g. the milestone
 *  caller) preserve its own pre-existing testid contract without
 *  forking the dialog. Each key defaults to the `lifecycle-*` scheme
 *  used by the shared dialog's own tests. */
export interface LifecycleTransitionDialogTestIds {
  /** Outer container testid. */
  modal?: string;
  /** Prefix applied to each outcome row: `${outcomePrefix}-${outcome.id}`. */
  outcomePrefix?: string;
  save?: string;
  dateInput?: string;
  commentInput?: string;
}

export interface LifecycleTransitionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  /** Rendered inside the context paragraph (e.g. "under-review"). */
  currentStatusLabel: string;
  /** Optional suffix appended to the context line. */
  contextSuffix?: string;
  /** Empty array → terminal mode; renders terminalCopy / reopenAction. */
  outcomes: Outcome[];
  /** Terminal-state copy when no outcomes are available. */
  terminalCopy?: string;
  /** Re-open affordance for terminal states; gated behind confirmation. */
  reopenAction?: { label: string; onConfirm: () => Promise<unknown> };
  /** Earliest date the date input will accept (`>= minDate`). */
  minDate?: string;
  /** Default value for the date input. Callers pass Brisbane-today. */
  todayIso: string;
  softWarningCtx?: SoftWarningCtx;
  onSubmit: (
    outcome: Outcome,
    payload: { date: string; comment?: string },
  ) => Promise<unknown>;
  /** Per-element testid overrides — see LifecycleTransitionDialogTestIds. */
  testIds?: LifecycleTransitionDialogTestIds;
}

const COMMENT_MAX_LEN = 1000;

function commentFieldLabel(outcome: Outcome | null): string {
  if (!outcome) return "Comment (optional)";
  if (outcome.commentLabel === "conditions") return "Comment (conditions)";
  if (outcome.commentLabel === "reason") return "Comment (reason)";
  return "Comment (optional)";
}

export default function LifecycleTransitionDialog({
  isOpen,
  onClose,
  title,
  currentStatusLabel,
  contextSuffix,
  outcomes,
  terminalCopy,
  reopenAction,
  minDate,
  todayIso,
  softWarningCtx,
  onSubmit,
  testIds,
}: LifecycleTransitionDialogProps) {
  const ids = {
    modal: testIds?.modal ?? "lifecycle-transition-dialog",
    outcomePrefix: testIds?.outcomePrefix ?? "lifecycle-outcome",
    save: testIds?.save ?? "lifecycle-save",
    dateInput: testIds?.dateInput ?? "lifecycle-date-input",
    commentInput: testIds?.commentInput ?? "lifecycle-comment-input",
  };
  const [selectedId, setSelectedId] = useState<string | null>(
    outcomes[0]?.id ?? null,
  );
  const [date, setDate] = useState<string>(todayIso);
  const [comment, setComment] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [reopenConfirming, setReopenConfirming] = useState(false);
  const [reopenSubmitting, setReopenSubmitting] = useState(false);

  // Reset state every time the dialog (re)opens or its outcome set changes —
  // keeps the form clean across multiple transitions on the same or different rows.
  // Computed during render (Pattern 0) so no setState-in-effect cascade.
  const seedKey = isOpen
    ? `${todayIso}:${outcomes.map((o) => o.id).join(",")}`
    : null;
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (seedKey !== null && seedKey !== seededFor) {
    setSeededFor(seedKey);
    setSelectedId(outcomes[0]?.id ?? null);
    setDate(todayIso);
    setComment("");
    setError(null);
    setSubmitting(false);
    setReopenConfirming(false);
    setReopenSubmitting(false);
  }

  const selectedOutcome = useMemo(
    () => outcomes.find((o) => o.id === selectedId) ?? null,
    [outcomes, selectedId],
  );

  const softWarningMessage = useMemo(() => {
    if (!selectedOutcome?.softWarning) return null;
    return selectedOutcome.softWarning(softWarningCtx ?? {});
  }, [selectedOutcome, softWarningCtx]);

  const handleSubmit = async () => {
    if (!selectedOutcome) return;
    const trimmed = comment.trim();
    if (selectedOutcome.requiresComment && trimmed.length === 0) {
      const what = selectedOutcome.commentLabel ?? "comment";
      setError(`${what.charAt(0).toUpperCase() + what.slice(1)} is required.`);
      return;
    }
    if (minDate && date < minDate) {
      setError(`Date must be on or after ${minDate}.`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload: { date: string; comment?: string } = { date };
      if (trimmed.length > 0) payload.comment = trimmed;
      await onSubmit(selectedOutcome, payload);
      onClose();
    } catch (err) {
      setSubmitting(false);
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : "Save failed");
      }
    }
  };

  const handleReopenConfirm = async () => {
    if (!reopenAction) return;
    setReopenSubmitting(true);
    setError(null);
    try {
      await reopenAction.onConfirm();
      onClose();
    } catch (err) {
      setReopenSubmitting(false);
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : "Re-open failed");
      }
    }
  };

  const isTerminal = outcomes.length === 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="space-y-4" data-testid={ids.modal}>
        <p
          className="text-body-sm text-slate-600 dark:text-slate-300"
          data-testid="lifecycle-context"
        >
          Current status:{" "}
          <span className="font-medium text-slate-900 dark:text-slate-100">
            {currentStatusLabel}
          </span>
          {contextSuffix ? ` · ${contextSuffix}` : ""}
          {isTerminal ? "" : ". Choose the outcome and the date it occurred."}
        </p>

        {isTerminal ? (
          <>
            {terminalCopy && (
              <p
                className="text-body-sm text-slate-600 dark:text-slate-300"
                data-testid="lifecycle-terminal-copy"
              >
                {terminalCopy}
              </p>
            )}
            {reopenAction && !reopenConfirming && (
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={onClose}>
                  Close
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setReopenConfirming(true)}
                  data-testid="lifecycle-reopen-button"
                >
                  {reopenAction.label}
                </Button>
              </div>
            )}
            {reopenAction && reopenConfirming && (
              <>
                <Alert variant="warning">
                  Re-opening this item will allow further lifecycle
                  transitions. The audit trail of the prior terminal
                  state is preserved.
                </Alert>
                {error && (
                  <Alert variant="error">
                    <span data-testid="lifecycle-error">{error}</span>
                  </Alert>
                )}
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => setReopenConfirming(false)}
                    disabled={reopenSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleReopenConfirm}
                    disabled={reopenSubmitting}
                    data-testid="lifecycle-reopen-confirm"
                  >
                    {reopenSubmitting ? "Re-opening…" : "Confirm re-open"}
                  </Button>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <fieldset className="space-y-2">
              <legend className="sr-only">Outcome</legend>
              {outcomes.map((opt) => (
                <label
                  key={opt.id}
                  data-testid={`${ids.outcomePrefix}-${opt.id}`}
                  className="flex items-start gap-3 rounded-md border border-slate-200 dark:border-slate-700 p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/40"
                >
                  <input
                    type="radio"
                    name="lifecycle-outcome"
                    value={opt.id}
                    checked={selectedId === opt.id}
                    onChange={() => setSelectedId(opt.id)}
                    className="mt-1 accent-brand-600"
                  />
                  <span className="flex-1">
                    <span className="flex items-center gap-2">
                      <span className="text-body-sm font-medium text-slate-900 dark:text-slate-100">
                        {opt.label}
                      </span>
                      <span className={`badge-${opt.pill}`}>{opt.pillLabel}</span>
                    </span>
                    <span className="mt-1 block text-caption text-slate-600 dark:text-slate-300">
                      {opt.description}
                    </span>
                  </span>
                </label>
              ))}
            </fieldset>

            <div>
              <label
                htmlFor="lifecycle-comment"
                className="block text-body-sm text-slate-700 dark:text-slate-200"
                data-testid="lifecycle-comment-label"
              >
                {commentFieldLabel(selectedOutcome)}
              </label>
              <textarea
                id="lifecycle-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                maxLength={COMMENT_MAX_LEN}
                placeholder="Add context for this transition (visible in the lifecycle history)"
                className="input mt-1 w-full"
                aria-describedby="lifecycle-comment-counter"
                data-testid={ids.commentInput}
              />
              <p
                id="lifecycle-comment-counter"
                className="mt-1 text-caption text-slate-500 dark:text-slate-400"
              >
                {comment.length}/{COMMENT_MAX_LEN}
              </p>
            </div>

            <div>
              <label
                htmlFor="lifecycle-date"
                className="block text-body-sm text-slate-700 dark:text-slate-200"
              >
                Date
              </label>
              <input
                id="lifecycle-date"
                type="date"
                value={date}
                min={minDate}
                onChange={(e) => setDate(e.target.value)}
                className="input mt-1 w-full"
                data-testid={ids.dateInput}
              />
              {minDate && (
                <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
                  Must be on or after {minDate}.
                </p>
              )}
            </div>

            {softWarningMessage && (
              <Alert variant="warning">
                <span data-testid="lifecycle-soft-warning">
                  {softWarningMessage}
                </span>
              </Alert>
            )}

            {error && (
              <Alert variant="error">
                <span data-testid="lifecycle-error">{error}</span>
              </Alert>
            )}

            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={submitting || !selectedOutcome}
                data-testid={ids.save}
              >
                {submitting ? "Saving…" : "Save"}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
